import React, { useState, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, useTheme,
} from "@mui/material";
import PageHeader from "../../components/ui/PageHeader";
import { useCollection } from "../../hooks/useCollection";
import EmptyState from "../../components/ui/EmptyState";
import LoadingSpinner from "../../components/ui/LoadingSpinner";

const BalancePage = () => {
  const theme = useTheme();
  const { data: journalEntries, loading: loadingEntries } = useCollection("journalEntries");
  const { data: accounts } = useCollection("chartOfAccounts");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const balanceData = useMemo(() => {
    if (!journalEntries || !accounts) return [];

    const accountBalances = {};

    // Inicializar todas las cuentas
    accounts
      .filter((a) => a.isActive !== false)
      .forEach((account) => {
        accountBalances[account.code] = {
          code: account.code,
          name: account.name,
          type: account.type,
          debit: 0,
          credit: 0,
        };
      });

    // Acumular movimientos
    journalEntries
      .filter((entry) => entry.isActive !== false)
      .forEach((entry) => {
        // Filtrar por fechas
        if (startDate && entry.date < startDate) return;
        if (endDate && entry.date > endDate) return;

        if (entry.lines) {
          entry.lines.forEach((line) => {
            if (accountBalances[line.accountCode]) {
              accountBalances[line.accountCode].debit += line.debit || 0;
              accountBalances[line.accountCode].credit += line.credit || 0;
            }
          });
        }
      });

    return Object.values(accountBalances)
      .filter((acc) => acc.debit > 0 || acc.credit > 0)
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [journalEntries, accounts, startDate, endDate]);

  const totals = useMemo(() => {
    return balanceData.reduce(
      (acc, accData) => ({
        debit: acc.debit + accData.debit,
        credit: acc.credit + accData.credit,
      }),
      { debit: 0, credit: 0 }
    );
  }, [balanceData]);

  if (loadingEntries) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader
        title="Balance de Sumas y Saldos"
        subtitle="Comprobación de totales por cuenta contable"
      />

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 150 }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 150 }}
          />
        </Box>
      </Paper>

      {balanceData.length === 0 ? (
        <EmptyState title="Sin datos" description="No hay movimientos en el período seleccionado" />
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cuenta</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Debe</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Haber</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo Deudor</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo Acreedor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balanceData.map((row) => {
                  const saldo = row.debit - row.credit;
                  return (
                    <TableRow key={row.code} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">{row.debit.toFixed(2)}€</TableCell>
                      <TableCell align="right">{row.credit.toFixed(2)}€</TableCell>
                      <TableCell align="right" sx={{ color: saldo > 0 ? 'success.main' : 'text.primary' }}>
                        {saldo > 0 ? saldo.toFixed(2) : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ color: saldo < 0 ? 'error.main' : 'text.primary' }}>
                        {saldo < 0 ? Math.abs(saldo).toFixed(2) : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper elevation={0} sx={{ p: 2, borderRadius: "16px", border: "1px solid", borderColor: theme.palette.divider, display: "flex", justifyContent: "flex-end", gap: 4 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Debe</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">{totals.debit.toFixed(2)}€</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Haber</Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">{totals.credit.toFixed(2)}€</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Diferencia</Typography>
              <Typography variant="h6" fontWeight={700} color={Math.abs(totals.debit - totals.credit) < 0.01 ? 'success.main' : 'error.main'}>
                {Math.abs(totals.debit - totals.credit) < 0.01 ? 'Cuadra ✓' : 'NO CUADRA ✗'}
              </Typography>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default BalancePage;